import { path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { NotificationChannel, notificationSchema } from '../notification'
import { manyItemsPaginatedByKey, paginationQueryParamsSchema } from '../shared'

export const notificationHistoryPathParamsSchema = z.object({
  orgCode: z.string()
})

export type NotificationHistoryPathParams = z.infer<typeof notificationHistoryPathParamsSchema>

export const channelsCodec = z.codec(z.string(), z.array(z.enum(NotificationChannel)), {
  encode: (val) => val.join(','),
  decode: (val) =>
    val
      .split(',')
      .map((c) => c.trim() as NotificationChannel)
      .filter((c) => c.length > 0)
})

export const notificationHistoryQueryParamsSchema = paginationQueryParamsSchema.extend({
  channels: channelsCodec
})

export type NotificationHistoryQueryParams = z.infer<typeof notificationHistoryQueryParamsSchema>

const responseSchema = manyItemsPaginatedByKey(notificationSchema.extend({ read: z.boolean() }))

export type NotificationHistoryResponse = z.infer<typeof responseSchema>

export const getNotificationHistoryApi = defineApi({
  pathSchema: notificationHistoryPathParamsSchema,
  querySchema: notificationHistoryQueryParamsSchema,
  pathFor: (params) =>
    path(`/notifications/history/${params.orgCode}`, {
      continueFrom: params.continueFrom,
      limit: params.limit,
      channels: params.channels
    }),
  gatewayPath: '/history/:orgCode' as const,
  name: 'GetNotificationHistory',
  description:
    'Get paginated notification history for an organization filtered by notification channels',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema
})
